import * as T from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as cloneSkinned } from 'three/examples/jsm/utils/SkeletonUtils.js';
import type { CyclingDiscipline } from '../model';
import type { BikeModel } from '../bike';
import { crashPose } from '../crash';

/**
 * Blender-built bikes (public/assets/bikes/<discipline>.glb) and the rigged rider (rider.glb).
 * Real-world scale; drive side on -X (the rider's right); +Z forward. Node and clip names follow
 * public/assets/bikes/manifest.json.
 */
interface BikeSpec { wheelRadius?: number; pivotHeight?: number }
export interface BikeLibrary { bikes: Partial<Record<CyclingDiscipline, T.Group>>; specs: Partial<Record<CyclingDiscipline, BikeSpec>>; rider: { scene: T.Group; clips: T.AnimationClip[] } | null }
const BASE = `${import.meta.env.BASE_URL}assets/bikes/`;
let pending: Promise<BikeLibrary> | null = null; let ready: BikeLibrary | null = null;
export function bikeLibraryNow() { return ready; }
export function loadBikeLibrary(anisotropy: number): Promise<BikeLibrary> {
  pending ??= (async () => {
    const lib: BikeLibrary = { bikes: {}, specs: {}, rider: null };
    let manifest: Record<string, unknown> = {};
    try { const r = await fetch(`${BASE}manifest.json`); if (r.ok) manifest = await r.json(); } catch { /* offline */ }
    const loader = new GLTFLoader();
    const tune = (root: T.Object3D) => root.traverse(o => { if (o instanceof T.Mesh) { o.castShadow = true; o.receiveShadow = true; for (const m of [o.material].flat()) for (const k of ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap'] as const) { const t = (m as T.MeshStandardMaterial)[k]; if (t) t.anisotropy = anisotropy; } } });
    await Promise.all((['mtb', 'road', 'bmx'] as const).map(async d => {
      try {
        const gltf = await loader.loadAsync(`${BASE}${d}.glb`); tune(gltf.scene); lib.bikes[d] = gltf.scene;
        const bikes = (manifest.bikes ?? manifest) as Record<string, Record<string, unknown>>, spec = bikes?.[d] ?? {};
        const wheel = gltf.scene.getObjectByName('WheelRear');
        const contact = (spec.contactPoints ?? spec) as Record<string, unknown>;
        lib.specs[d] = { wheelRadius: typeof contact.wheelRadius === 'number' ? contact.wheelRadius : wheel?.position.y ?? .37 };
      } catch { /* not built: procedural bike remains */ }
    }));
    try { const gltf = await loader.loadAsync(`${BASE}rider.glb`); tune(gltf.scene); lib.rider = { scene: gltf.scene, clips: gltf.animations }; } catch { /* procedural rider remains */ }
    ready = lib; return lib;
  })();
  return pending;
}

function numberTexture(n: number) {
  const canvas = document.createElement('canvas'); canvas.width = 256; canvas.height = 192; const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#f3f1ea'; ctx.fillRect(0, 0, 256, 192); ctx.fillStyle = '#1d272b'; ctx.textAlign = 'center';
  ctx.font = '900 112px Arial'; ctx.fillText(String(n).padStart(2, '0'), 128, 118); ctx.font = 'bold 28px Arial'; ctx.fillText('HARTEX', 128, 166);
  const t = new T.CanvasTexture(canvas); t.colorSpace = T.SRGBColorSpace; t.anisotropy = 4; return t;
}
const smoothstep = (a: number, b: number, x: number) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

/** Same contract as the procedural makeBike(): world.ts places, leans and crashes it identically. */
export function makeAssetBike(lib: BikeLibrary, d: CyclingDiscipline, color: string, kitColor: string, riderNumber: number): BikeModel | null {
  const source = lib.bikes[d]; if (!source) return null;
  const r = lib.specs[d]?.wheelRadius ?? .37, pivotHeight = .85;
  const group = new T.Group(), pivot = new T.Group(), chassis = new T.Group(), ejection = new T.Group();
  group.rotation.order = 'YXZ'; pivot.position.y = pivotHeight; chassis.position.y = -pivotHeight; pivot.add(chassis); group.add(pivot, ejection);
  const bike = source.clone(true); chassis.add(bike);
  // Metallic flake paint under a deep clearcoat; one material per bike so rivals keep their own colours.
  let frameMat: T.MeshPhysicalMaterial | null = null;
  bike.traverse(o => {
    if (!(o instanceof T.Mesh)) return;
    const m = o.material as T.MeshStandardMaterial;
    if (m.name === 'Paint') {
      frameMat ??= new T.MeshPhysicalMaterial({ color, metalness: .45, roughness: .3, clearcoat: 1, clearcoatRoughness: .05, normalMap: m.normalMap, roughnessMap: m.roughnessMap, name: 'Paint' });
      o.material = frameMat;
    }
  });
  frameMat ??= new T.MeshPhysicalMaterial({ color });
  const node = (name: string) => bike.getObjectByName(name) ?? new T.Group();
  const wheels = [node('WheelRear'), node('WheelFront')] as T.Group[], cranks = node('Cranks'), pedals = [node('PedalL'), node('PedalR')];
  const wheelRest = wheels.map(w => ({ parent: w.parent!, position: w.position.clone(), quaternion: w.quaternion.clone() }));
  let riderRoot: T.Object3D | null = null, mixer: T.AnimationMixer | null = null;
  const actions = new Map<string, T.AnimationAction>(), weights = new Map<string, number>();
  if (lib.rider) {
    riderRoot = cloneSkinned(lib.rider.scene); riderRoot.name = 'rider';
    const jersey = new Map<T.Material, T.Material>();
    riderRoot.traverse(o => {
      if (!(o instanceof T.Mesh)) return;
      o.castShadow = true; o.frustumCulled = false;
      const m = o.material as T.MeshStandardMaterial;
      if (m.name === 'Jersey') { if (!jersey.has(m)) { const c = m.clone(); c.color.set(kitColor); jersey.set(m, c); } o.material = jersey.get(m)!; }
      if (m.name === 'Number') { const c = m.clone(); c.map = numberTexture(riderNumber); c.color.set('#ffffff'); o.material = c; }
    });
    chassis.add(riderRoot);
    mixer = new T.AnimationMixer(riderRoot);
    for (const clip of lib.rider.clips) { const a = mixer.clipAction(clip); a.play(); a.timeScale = 0; a.setEffectiveWeight(0); actions.set(clip.name, a); weights.set(clip.name, 0); }
  }
  const pick = (...names: string[]) => names.find(n => actions.has(n));
  let pedalPhase = 0, lastDistance = 0, lastTime = performance.now(), crashed = false;
  const animate: BikeModel['animate'] = (distance, lean, air, trick, progress = 0, crash = null, pedalling = true, sprint = false) => {
    const now = performance.now(), dt = Math.min(.1, (now - lastTime) / 1000); lastTime = now;
    const travelled = distance - lastDistance; lastDistance = distance;
    group.rotation.z = crash ? 0 : lean; group.rotation.x = 0;
    pivot.rotation.set(0, 0, 0); pivot.position.set(0, pivotHeight, 0);
    const pose = Math.sin(Math.PI * Math.min(1, progress));
    if (air > 0) { if (trick === 1) pivot.rotation.z = pose * .95; if (trick === 2) pivot.rotation.y = pose * .85; if (trick === 5) pivot.rotation.y = progress * Math.PI * 2; if (trick === 6) pivot.rotation.x = progress * Math.PI * 2; if (trick === 7) pivot.rotation.x = -progress * Math.PI * 2; }
    // Cadence follows wheel speed through a real gear, but legs top out around 115 rpm.
    if (pedalling && air === 0 && !crash) {
      const development = d === 'road' ? 7.2 : d === 'bmx' ? 5.4 : 4.6, revs = Math.abs(travelled) / development;
      pedalPhase += Math.min(revs, 1.9 * dt) * Math.PI * 2;
    } else if (!crash) {
      // Coasting: the coast pose has the drive-side crank forward (π/2), so ease the cranks level.
      const level = Math.PI / 2 + Math.round((pedalPhase - Math.PI / 2) / (Math.PI * 2)) * Math.PI * 2;
      pedalPhase += (level - pedalPhase) * (1 - Math.exp(-dt * 8));
    }
    cranks.rotation.x = pedalPhase; for (const p of pedals) p.rotation.x = -pedalPhase;

    if (!crash) wheels.forEach((w, i) => {
      if (w.parent !== wheelRest[i].parent) wheelRest[i].parent.add(w);
      w.position.copy(wheelRest[i].position); w.quaternion.copy(wheelRest[i].quaternion); w.rotateX(distance / r); w.scale.setScalar(1);
    });
    if (mixer && riderRoot) {
      // Choose the target pose, then cross-fade weights; pedalling clips are scrubbed by the crank angle.
      const landed = crash && crashPose(crash).landed;
      const target = crash ? pick(landed ? 'crash_lie' : 'crash_tumble') :
        air > 0 && trick === 3 ? pick('trick_nohander') : air > 0 && trick === 4 ? pick('trick_superman') : air > 0 && trick === 1 ? pick('trick_tabletop') :
        pedalling && air === 0 ? pick(sprint ? `sprint_${d}` : `pedal_${d}`, `pedal_${d}`) : pick(`coast_${d}`, `pedal_${d}`);
      const phase = ((pedalPhase / (Math.PI * 2)) % 1 + 1) % 1, k = 1 - Math.exp(-dt * (crash ? 14 : 9));
      for (const [name, action] of actions) {
        const w = weights.get(name)! + ((name === target ? 1 : 0) - weights.get(name)!) * k;
        weights.set(name, w < .002 ? 0 : w); action.setEffectiveWeight(w);
        const clip = action.getClip();
        action.time = name.startsWith('pedal_') || name.startsWith('sprint_') ? phase * clip.duration : name === 'crash_tumble' && crash ? (crash.time % clip.duration) : Math.min(clip.duration, name.startsWith('trick_') ? clip.duration * smoothstep(0, .35, progress) : 0);
      }
      mixer.update(0);
      if (!!crash !== crashed) {
        if (crash) { ejection.attach(riderRoot); } else { chassis.add(riderRoot); riderRoot.position.set(0, 0, 0); riderRoot.rotation.set(0, 0, 0); ejection.rotation.set(0, 0, 0); ejection.position.set(0, 0, 0); }
        crashed = !!crash;
      }
    }
    if (crash) {
      const c = crashPose(crash), t = crash.time, spin = Math.min(1, t / 1.35);
      if (riderRoot) { riderRoot.position.set(0, 0, 0); riderRoot.rotation.set(0, 0, 0); }
      ejection.position.set(-c.sideways, c.height + (c.landed ? 0 : .55), c.forward);
      ejection.rotation.set(c.landed ? 0 : c.tumble * .35, Math.sin(t * 3) * .4, c.landed ? 0 : crash.direction * t * 1.2);
      pivot.rotation.set(spin * Math.PI * 1.85, spin * .8, crash.direction * spin * 1.4);
      pivot.position.set(-crash.direction * Math.min(1, t) * .7, .45 + Math.abs(Math.sin(t * 7)) * Math.max(0, 1 - t), Math.min(t, 1.4) * crash.speed * .09);
      wheels.forEach((w, i) => {
        if (w.parent !== group) group.add(w);
        const travel = Math.min(t, 1.8);
        w.position.set((i ? 1 : -1) * crash.direction * (.6 + travel * .7), r + Math.abs(Math.sin(t * 6 + i)) * Math.max(0, 1.4 - t), wheelRest[i].position.z + travel * crash.speed * (i ? .16 : .08));
        w.rotation.set(t * 14, t * (i ? 2 : -1), (i ? 1 : -1) * Math.min(1, t) * 1.2);
      });
    }
  };
  return { rider: ejection, wreck: pivot, group, wheels, frameMat, animate };
}
